import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ChevronDownIcon, MapPinIcon, CubeIcon } from '@heroicons/react/24/outline';
import {
  ChangeEvent,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-hot-toast';
import { Button } from '@follow/ui';
import AssetsService from '../../services/AssetsService';
import {
  Equipment,
  EquipmentPrice,
  EquipmentStateOfUse,
  EquipmentType,
  Lock,
  Station,
} from '../../services/models/assets';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalStorage } from 'usehooks-ts';
import { RentalDuration } from '../../services/models/rental';

interface ModalEquipmentProps {
  equipment?: Partial<Equipment>;
  visible: boolean;
  onClose: () => void;
}

const translatedUnit = (unit: string) => {
  switch (unit) {
    case 'minute':
      return 'minute';

    case 'hour':
      return 'heure';

    case 'day':
      return 'jour';

    case 'week':
      return 'semaine';

    case 'month':
      return 'mois';

    default:
      return unit;
  }
};

interface EquipmentPriceWithReadablePrice extends Omit<EquipmentPrice, 'id'> {
  readablePrice: string;
}

const inputClassName = 'w-full px-3 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small text-neutral focus:outline-primary-500';
const selectClassName = 'appearance-none w-full pl-3 pr-8 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 text-neutral typo-small cursor-pointer';

const ModalEquipment: React.FC<ModalEquipmentProps> = ({ equipment, visible, onClose }) => {
  const [updatedEquipment, setUpdatedEquipment] = useState<Partial<Equipment>>();
  const [newEquipmentPrices, setNewEquipmentPrices] = useState<EquipmentPriceWithReadablePrice[]>([]);
  const [deposit, setDeposit] = useState<string>('');

  const equipmentInput = useRef<HTMLInputElement>(null);

  const [locationId] = useLocalStorage<number | undefined>('locationId', undefined);

  const queryClient = useQueryClient();

  const { data: rentalDurations } = useQuery({
    queryKey: ['rentalDurations'],
    queryFn: () => AssetsService.getRentalDurations(locationId!),
    select: (data) => data.rentalDurations,
    enabled: locationId != null,
  });

  const { data: stations } = useQuery({
    queryKey: ['stations', locationId],
    queryFn: () => AssetsService.getStations(locationId!),
    select: (data) => data.stations,
    enabled: visible && locationId != null && equipment?.id != null,
  });

  const saveEnabled = useMemo(() => (
    JSON.stringify(equipment) !== JSON.stringify(updatedEquipment)
    && (updatedEquipment?.model?.length ?? 0) > 0
    && (updatedEquipment?.description?.length ?? 0) > 0
    && (updatedEquipment?.deposit ?? 0) > 0
    && (updatedEquipment?.imageUrl?.length ?? 0) > 0
    || (newEquipmentPrices.length > 0 && newEquipmentPrices.some((equipmentPrice) => (
      equipmentPrice.price !== (equipment?.equipmentPrices?.find((ep) => ep.rentalDuration.id === equipmentPrice.rentalDuration.id)?.price ?? 0)))
    )
  ), [equipment, updatedEquipment, newEquipmentPrices]);

  useEffect(() => {
    setUpdatedEquipment(equipment?.id ? equipment : {
      model: '',
      description: '',
      deposit: 0,
      imageUrl: '',
      stateOfUse: EquipmentStateOfUse.NEW,
      type: EquipmentType.BIKE,
    });
    setDeposit(`€ ${((equipment?.deposit ?? 0) / 100)
      .toString()
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.\d\d).*/g, '$1')}`);
  }, [equipment]);

  useEffect(() => {
    if (equipment && rentalDurations) {
      setNewEquipmentPrices(rentalDurations.map((rentalDuration) => {
        const equipmentPrice = equipment?.equipmentPrices?.find((equipmentPrice) => equipmentPrice.rentalDuration.id === rentalDuration.id);

        return {
          ...(equipmentPrice ?? {}),
          price: equipmentPrice?.price ?? 0,
          readablePrice: `€ ${(equipmentPrice?.price ?? 0) / 100}`,
          rentalDuration,
        };
      }));
    }
  }, [rentalDurations, equipment]);

  const uploadImageMutation = useMutation({
    mutationFn: (image: File) => AssetsService.uploadImage(image),
    onSuccess: (response) => {
      setUpdatedEquipment((prev) => ({
        ...(prev ? prev : {}),
        imageUrl: response.url,
      }));
    },
    onError: () => {
      toast.error('Erreur lors de l\'envoi de l\'image');
    },
  });

  const updateEquipmentMutation = useMutation({
    mutationFn: ({ id, changes, prices }: { id: number; changes: Partial<Equipment>; prices: EquipmentPriceWithReadablePrice[] }) =>
      AssetsService.updateEquipment(id, changes, prices),
    onSuccess: () => {
      onClose();
      queryClient.resetQueries({ queryKey: ['stations'] });
      queryClient.resetQueries({ queryKey: ['equipments'] });
      toast.success('Équipement mis à jour.');
    },
    onError: (error: any) => {
      console.warn(error);
      if (error.message) {
        toast.error(error.message);
      }
    },
  });

  const createEquipmentMutation = useMutation({
    mutationFn: ({ data, prices }: { data: Partial<Equipment>; prices: EquipmentPriceWithReadablePrice[] }) =>
      AssetsService.createEquipment(data, prices),
    onSuccess: () => {
      onClose();
      queryClient.resetQueries({ queryKey: ['stations'] });
      queryClient.resetQueries({ queryKey: ['equipments'] });
      toast.success('Équipement créé.');
    },
    onError: (error: any) => {
      console.warn(error);
      if (error.message) {
        toast.error(error.message);
      }
    },
  });

  const moveEquipmentMutation = useMutation({
    mutationFn: ({ lockId, equipmentId }: { lockId: number; equipmentId?: number }) =>
      AssetsService.updateLockEquipment(lockId, equipmentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stations'] });
      queryClient.invalidateQueries({ queryKey: ['equipments'] });
      toast.success(variables.equipmentId != null ? 'Équipement déplacé.' : 'Équipement retiré du lock.');
    },
    onError: (error: any) => {
      console.warn(error);
      toast.error(error?.message ?? 'Erreur lors de la mise à jour de l\'emplacement.');
    },
  });

  const uploading = uploadImageMutation.isPending;
  const saving = updateEquipmentMutation.isPending || createEquipmentMutation.isPending;
  const moving = moveEquipmentMutation.isPending;

  // Flatten every lock across the location's stations, resolving its current
  // occupant so we can show availability and the equipment's own placement.
  const lockEntries = useMemo(() => {
    const entries: { lock: Lock; station: Station; occupant?: Equipment }[] = [];

    (stations ?? []).forEach((station) => {
      [...(station.ecus ?? [])]
        .sort((a, b) => a.address - b.address)
        .forEach((ecu) => {
          [...(ecu.locks ?? [])]
            .sort((a, b) => a.position - b.position)
            .forEach((lock) => {
              entries.push({ lock, station, occupant: lock.equipments?.[0] });
            });
        });
    });

    return entries;
  }, [stations]);

  const currentLockEntry = useMemo(
    () => lockEntries.find((entry) => entry.occupant?.id === equipment?.id),
    [lockEntries, equipment?.id],
  );

  // Prefer the placement resolved from the selected location's stations, but
  // fall back to the lock carried by the equipment prop so the current
  // emplacement still shows for equipments located outside that location (or
  // while the stations query is still loading).
  const currentLockLabel = useMemo(() => {
    if (currentLockEntry) {
      return `${currentLockEntry.station.name || `Station ${currentLockEntry.station.id}`} · Lock ${currentLockEntry.lock.position + 1}`;
    }
    if (equipment?.lock) {
      const stationName = equipment.lock.ecu?.station?.name;
      return `${stationName ? `${stationName} · ` : ''}Lock ${equipment.lock.position + 1}`;
    }
    return null;
  }, [currentLockEntry, equipment?.lock]);

  const onImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const image = event.target.files?.[0];

    if (image) {
      if (image.size > 5 * 1024 * 1024) {
        toast.error('L\'image est trop lourde (5Mb maximum)');
      } else if (!image.type.startsWith('image/')) {
        toast.error('Le fichier n\'est pas une image');
      } else {
        uploadImageMutation.mutate(image);
      }
    }
  };

  const onValueChange = (key: keyof Equipment, value: string | number) => {
    setUpdatedEquipment((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const onPriceChange = (rentalDuration: RentalDuration, value: string) => {
    const formattedPrice = `€ ${value
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.\d\d).*/g, '$1')}`;

    const equipmentPricesIndex = newEquipmentPrices.findIndex((e) => e.rentalDuration?.id === rentalDuration.id);

    setNewEquipmentPrices((prev) => [
      ...prev.slice(0, equipmentPricesIndex),
      {
        ...prev[equipmentPricesIndex],
        price: Math.round(parseFloat(formattedPrice.replace('€ ', '')) * 100),
        readablePrice: formattedPrice,
      },
      ...prev.slice(equipmentPricesIndex + 1),
    ]);
  };

  const onDepositChange = (value: string) => {
    let newValue = Math.round(parseFloat(value.replace('€ ', '')) * 100);

    if (isNaN(newValue)) {
      newValue = 0;
    }
    setUpdatedEquipment((prev) => {
      return {
        ...prev,
        deposit: newValue,
      };
    });
    setDeposit(`€ ${value
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/(\.\d\d).*/g, '$1')}`);
  };

  const onLockChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (!equipment?.id || moving) {
      return;
    }
    const value = event.target.value;

    if (value === '') {
      if (currentLockEntry) {
        moveEquipmentMutation.mutate({ lockId: currentLockEntry.lock.id });
      }
      return;
    }

    const lockId = Number(value);
    if (lockId === currentLockEntry?.lock.id) {
      return;
    }
    moveEquipmentMutation.mutate({ lockId, equipmentId: equipment.id });
  };

  const onSave = () => {
    if (updatedEquipment && equipment?.id) {
      const changes = Object.keys(updatedEquipment).reduce((acc, key) => {
        if (!['updatedAt', 'createdAt'].includes(key)
          && (JSON.stringify(equipment[key as keyof Equipment]) !== JSON.stringify(updatedEquipment[key as keyof Equipment]))) {
          acc[key as 'model'] = updatedEquipment[key as keyof Equipment] as string;
        }
        return acc;
      }, {} as Partial<Equipment>);
      updateEquipmentMutation.mutate({ id: equipment.id, changes, prices: newEquipmentPrices });
    } else if (updatedEquipment) {
      createEquipmentMutation.mutate({ data: updatedEquipment, prices: newEquipmentPrices });
    }
  };

  const busy = saving || uploading || moving;

  return (
    <Transition.Root show={visible} as={Fragment}>
      {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
      <Dialog as="div" className="relative z-50" onClose={!busy ? onClose : () => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-30 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white p-6 text-left shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100">
                      <CubeIcon className="h-5 w-5 text-primary-600" />
                    </div>
                    <Dialog.Title as="h3" className="text-neutral typo-h3">
                      {equipment?.id ? 'Modifier l\'équipement' : 'Nouvel équipement'}
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    className="rounded-md p-1 text-neutral-40 hover:text-neutral hover:bg-neutral-5 transition-colors focus:outline-none disabled:opacity-50"
                    onClick={onClose}
                    disabled={busy}
                  >
                    <span className="sr-only">Fermer</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                <form>
                  <div className="grid grid-cols-6 gap-5">
                    <div className="col-span-6">
                      <label htmlFor="image" className="block typo-small text-neutral-60 mb-2">
                        Image
                      </label>
                      {uploading ? (
                        <div className="flex w-full items-center justify-center h-56 bg-neutral-5 rounded-lg">
                          <p className="text-neutral-40 typo-small">Envoi de l'image...</p>
                        </div>
                      ) : (
                        <button
                          className="relative flex w-full items-center gap-3 justify-center hover:opacity-90"
                          onClick={(e) => {
                            e.preventDefault();
                            equipmentInput.current?.click();
                          }}
                        >
                          <input
                            type="file"
                            id="equipment"
                            name="image"
                            ref={equipmentInput}
                            className="hidden"
                            accept="image/*"
                            onChange={onImageChange}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          />
                          {updatedEquipment?.imageUrl ? (
                            <div className="w-full">
                              <img
                                src={updatedEquipment?.imageUrl}
                                alt="Équipement"
                                className="w-full h-56 bg-neutral-5 rounded-lg object-cover"
                              />
                              <p className="text-neutral typo-small !font-bold hover:underline mt-1">Changer l'image</p>
                            </div>
                          ) : (
                            <>
                              <div className="w-full h-56 bg-neutral-5 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-10" />
                              <div className="absolute flex flex-col items-center">
                                <p className="text-neutral typo-body-lg !font-bold">Choisir une image</p>
                                <p className="text-neutral-60 typo-small">jpg, jpeg, png - 5Mb maximum</p>
                              </div>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    <div className="col-span-6">
                      <label htmlFor="model" className="block typo-small text-neutral-60 mb-2">
                        Modèle
                      </label>
                      <input
                        type="text"
                        name="model"
                        id="model"
                        defaultValue={updatedEquipment?.model ?? ''}
                        onChange={(e) => onValueChange('model', e.target.value)}
                        className={inputClassName}
                      />
                    </div>
                    <div className="col-span-6">
                      <div className="flex justify-between mb-2">
                        <label className="block typo-small text-neutral-60" htmlFor="description">
                          Description
                        </label>
                        <span className="text-neutral-40 typo-small">{`${updatedEquipment?.description?.length ?? 0}/2000`}</span>
                      </div>
                      <textarea
                        className={`${inputClassName} min-h-24`}
                        id="description"
                        rows={4}
                        maxLength={2000}
                        defaultValue={updatedEquipment?.description ?? ''}
                        onChange={(e) => onValueChange('description', e.target.value)}
                      />
                    </div>
                    <div className="col-span-6 grid grid-cols-2 gap-4">
                      {rentalDurations?.map((rentalDuration) => (
                        <div key={rentalDuration.id} className="col-span-2 sm:col-span-1">
                          <label
                            htmlFor={`price-${rentalDuration.duration}-${rentalDuration.unit}`}
                            className="block typo-small text-neutral-60 mb-2"
                          >
                            Prix / {rentalDuration.duration > 1 ? `${rentalDuration.duration} ` : ''}{translatedUnit(rentalDuration.unit)}
                          </label>
                          <input
                            type="text"
                            name={`price-${rentalDuration.duration}-${rentalDuration.unit}`}
                            id={`price-${rentalDuration.duration}-${rentalDuration.unit}`}
                            value={(newEquipmentPrices.find((equipmentPrice) => equipmentPrice.rentalDuration.id === rentalDuration.id)?.readablePrice ?? '€ 0')}
                            onChange={(e) => onPriceChange(rentalDuration, e.target.value)}
                            className={inputClassName}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="col-span-6">
                      <label htmlFor="deposit" className="block typo-small text-neutral-60 mb-2">
                        Caution
                      </label>
                      <input
                        type="text"
                        name="deposit"
                        id="deposit"
                        value={deposit}
                        className={inputClassName}
                        onChange={(e) => onDepositChange(e.target.value)}
                      />
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="stateOfUse" className="block typo-small text-neutral-60 mb-2">
                        État d'utilisation
                      </label>
                      <div className="relative">
                        <select
                          id="stateOfUse"
                          name="stateOfUse"
                          className={selectClassName}
                          value={updatedEquipment?.stateOfUse ?? EquipmentStateOfUse.NEW}
                          onChange={(e) => onValueChange('stateOfUse', e.target.value)}
                        >
                          <option value={EquipmentStateOfUse.NEW}>Neuf</option>
                          <option value={EquipmentStateOfUse.USED}>Usagé</option>
                        </select>
                        <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-40" strokeWidth={3} />
                      </div>
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="type" className="block typo-small text-neutral-60 mb-2">
                        Type
                      </label>
                      <div className="relative">
                        <select
                          id="type"
                          name="type"
                          className={selectClassName}
                          value={updatedEquipment?.type ?? EquipmentType.BIKE}
                          onChange={(e) => onValueChange('type', e.target.value)}
                        >
                          <option value={EquipmentType.SURF}>Surf</option>
                          <option value={EquipmentType.PADDLE}>Paddle</option>
                          <option value={EquipmentType.KAYAK}>Kayak</option>
                          <option value={EquipmentType.BIKE}>Vélo</option>
                        </select>
                        <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-40" strokeWidth={3} />
                      </div>
                    </div>
                  </div>
                </form>

                {equipment?.id && (
                  <div className="mt-5 p-4 bg-off-white rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="w-5 h-5 text-primary-500" />
                      <h3 className="text-neutral typo-h3">Emplacement</h3>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="typo-small text-neutral-60">Lock actuel :</span>
                      {currentLockLabel ? (
                        <span className="typo-small text-neutral !font-semibold">
                          {currentLockLabel}
                        </span>
                      ) : (
                        <span className="typo-small text-neutral-40">Aucun emplacement</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="lock" className="block typo-small text-neutral-60">
                        Déplacer vers un autre lock
                      </label>
                      <div className="relative">
                        <select
                          id="lock"
                          name="lock"
                          className={`${selectClassName} bg-white`}
                          value={currentLockEntry ? String(currentLockEntry.lock.id) : ''}
                          onChange={onLockChange}
                          disabled={moving || lockEntries.length === 0}
                        >
                          <option value="">— Aucun emplacement —</option>
                          {(stations ?? []).map((station) => (
                            <optgroup key={station.id} label={station.name || `Station ${station.id}`}>
                              {lockEntries
                                .filter((entry) => entry.station.id === station.id)
                                .map(({ lock, occupant }) => {
                                  const occupiedByOther = occupant != null && occupant.id !== equipment.id;
                                  return (
                                    <option key={lock.id} value={String(lock.id)} disabled={occupiedByOther}>
                                      Lock {lock.position + 1}
                                      {occupiedByOther ? ` · occupé (${occupant?.model})` : ''}
                                    </option>
                                  );
                                })}
                            </optgroup>
                          ))}
                        </select>
                        <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-40" strokeWidth={3} />
                      </div>
                      <p className="typo-caption text-neutral-40">
                        Sélectionnez « Aucun emplacement » pour retirer l'équipement de son lock.
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
                    Annuler
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    tone="light"
                    onClick={onSave}
                    loading={saving}
                    disabled={!saveEnabled || busy}
                  >
                    Sauvegarder
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalEquipment;
